import React, { useEffect, useMemo, useState } from "react";
import { Grid, MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@material-ui/core";
import { Breadcrumb } from "components/breadCrumb";
import { PageTitle } from "components/pageTitle";
import { Wrapper } from "components/wrapper";
import CustomBackdrop from "components/backdrop";
import MunicipalPortalAAppService from "application/platform/municipal-portal-service";
import { STATUS_PRESENCA, TIPO_SESSAO, formatDate, labelOf } from "./camara-labels";

interface PresencaSessao {
  id: number;
  idSessaoLegislativa: number;
  idVereador: number;
  indStatus: number;
  nomVereador: string;
  dtaSessao: string;
  indTipoSessao?: number;
}

const PRESENTE = 1;
const AUSENTE = 2;
const JUSTIFICADA = 3;

export function PresencasSessaoPage() {
  const [presencas, setPresencas] = useState<PresencaSessao[]>([]);
  const [loading, setLoading] = useState(true);
  const [sessaoId, setSessaoId] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    (async () => {
      try {
        setPresencas(await MunicipalPortalAAppService.listPresencasSessao());
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // A API já devolve da sessão mais recente para a mais antiga; mantém essa ordem.
  const sessoes = useMemo(() => {
    const seen = new Map<number, PresencaSessao>();
    presencas.forEach((p) => {
      if (!seen.has(p.idSessaoLegislativa)) seen.set(p.idSessaoLegislativa, p);
    });
    return Array.from(seen.values());
  }, [presencas]);

  const selectedId = sessaoId || (sessoes[0] ? String(sessoes[0].idSessaoLegislativa) : "");
  const daSessao = presencas.filter((p) => String(p.idSessaoLegislativa) === selectedId);

  const resumo = useMemo(() => {
    const map = new Map<number, { nome: string; presentes: number; ausentes: number; justificadas: number; total: number }>();
    presencas.forEach((p) => {
      const item = map.get(p.idVereador) || { nome: p.nomVereador, presentes: 0, ausentes: 0, justificadas: 0, total: 0 };
      item.total += 1;
      if (p.indStatus === PRESENTE) item.presentes += 1;
      if (p.indStatus === AUSENTE) item.ausentes += 1;
      if (p.indStatus === JUSTIFICADA) item.justificadas += 1;
      map.set(p.idVereador, item);
    });
    return Array.from(map.values()).sort((a, b) => a.nome.localeCompare(b.nome));
  }, [presencas]);

  return (
    <>
      <Breadcrumb items={[{ label: "Home", href: "/" }, { label: "Presença em Sessões" }]} />
      <PageTitle>PRESENÇA EM SESSÕES</PageTitle>
      <Wrapper>
        <CustomBackdrop open={loading} />
        {!loading && presencas.length === 0 && <Typography variant="body1">Nenhuma presença registrada até o momento.</Typography>}

        {presencas.length > 0 && (
          <Grid container spacing={4}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                select
                variant="outlined"
                fullWidth
                label="Sessão"
                value={selectedId}
                onChange={(e) => setSessaoId(e.target.value)}
                InputLabelProps={{ shrink: true }}
              >
                {sessoes.map((s) => (
                  <MenuItem key={s.idSessaoLegislativa} value={String(s.idSessaoLegislativa)}>
                    {formatDate(s.dtaSessao)} - {labelOf(TIPO_SESSAO, s.indTipoSessao)}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Presença na sessão selecionada
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vereador</TableCell>
                      <TableCell>Situação</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {daSessao.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell>{p.nomVereador}</TableCell>
                        <TableCell>{labelOf(STATUS_PRESENCA, p.indStatus)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>

            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Frequência por vereador (todas as sessões)
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Vereador</TableCell>
                      <TableCell align="center">Sessões registradas</TableCell>
                      <TableCell align="center">Presenças</TableCell>
                      <TableCell align="center">Ausências</TableCell>
                      <TableCell align="center">Justificadas</TableCell>
                      <TableCell align="center">% de presença</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {resumo.map((r) => (
                      <TableRow key={r.nome}>
                        <TableCell>{r.nome}</TableCell>
                        <TableCell align="center">{r.total}</TableCell>
                        <TableCell align="center">{r.presentes}</TableCell>
                        <TableCell align="center">{r.ausentes}</TableCell>
                        <TableCell align="center">{r.justificadas}</TableCell>
                        <TableCell align="center">{r.total ? `${Math.round((r.presentes / r.total) * 100)}%` : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
          </Grid>
        )}
      </Wrapper>
    </>
  );
}
